import { useEffect, useRef, useState } from 'react';
import { useLiveFeed } from '../../hooks/useLiveFeed.js';
import { useDashboard } from '../../hooks/useDashboard.js';
import { useJobs } from '../../hooks/useJobs.js';
import { useGeofenceViolations } from '../../hooks/useGeofenceViolations.js';
import { centsToUsd, formatDuration } from '../../lib/format.js';

// Live operations view: realtime-ish event feed (10s poll) on the left,
// Leaflet map on the right showing currently-clocked-in crew + job sites
// + recent geofence violations.
//
// Leaflet is loaded from a CDN inside useEffect — it's not bundled. That
// also means the page degrades gracefully when offline: the feed still
// works, the map shows an "unavailable" placeholder. Coordinate visibility
// is already gated server-side via scopedUserIds.
export default function LivePage() {
  const { data: feed, isLoading: feedLoading } = useLiveFeed();
  const { data: dashboard } = useDashboard();
  const { data: jobs = [] } = useJobs('active');
  const { data: violations = [] } = useGeofenceViolations({
    from: Date.now() - 24 * 3_600_000,
    to: Date.now(),
  });

  return (
    <div className="live-grid">
      <section className="live-feed-col">
        <h3 className="panel-title">
          <span><i className="ph ph-broadcast" /> Live feed</span>
          <span style={{ fontSize: 11, color: 'var(--warm-gray)' }}>
            {feed?.events?.length || 0} events / 24h
          </span>
        </h3>
        {feedLoading ? (
          <div className="loading">Loading…</div>
        ) : !feed?.events?.length ? (
          <div className="loading">No activity in the last 24 hours.</div>
        ) : (
          <ul className="event-feed">
            {feed.events.map((e, i) => <FeedItem key={`${e.kind}-${e.at}-${i}`} e={e} />)}
          </ul>
        )}
      </section>
      <section className="live-map-col panel">
        <h3 className="panel-title">
          <span><i className="ph ph-map-trifold" /> Map</span>
          <span style={{ fontSize: 11, color: 'var(--warm-gray)' }}>
            {(dashboard?.live || []).length} on shift
          </span>
        </h3>
        <LiveMap
          openShifts={dashboard?.live || []}
          jobs={jobs}
          violations={violations}
        />
      </section>
    </div>
  );
}

const KIND_META = {
  clock_in:           { icon: 'ph-sign-in',         label: 'Clocked in',         color: 'var(--success, #4ade80)' },
  clock_out:          { icon: 'ph-sign-out',        label: 'Clocked out',        color: 'var(--warm-gray)' },
  geofence_violation: { icon: 'ph-warning',         label: 'Geofence violation', color: 'var(--error)' },
  expense_submitted:  { icon: 'ph-receipt',         label: 'Expense submitted',  color: 'var(--orange)' },
};

function FeedItem({ e }) {
  const meta = KIND_META[e.kind] || { icon: 'ph-circle', label: e.kind, color: 'var(--warm-gray)' };
  return (
    <li className="event-row">
      <div className="event-icon" style={{ color: meta.color }}>
        <i className={'ph ' + meta.icon} />
      </div>
      <div className="event-body">
        <div className="event-headline">
          <strong>{e.user_name}</strong> · {meta.label}
          {e.job_name ? <> · {e.job_name}</> : null}
        </div>
        <div className="event-sub">
          {fmtRelative(e.at)}
          {e.kind === 'clock_out' && e.meta?.duration_ms != null && (
            <> · shift {formatDuration(e.meta.duration_ms)}</>
          )}
          {e.kind === 'geofence_violation' && e.meta?.distance_m != null && (
            <> · {e.meta.distance_m}m from site (allowed {e.meta.allowed_m}m)
              {e.meta.actor_name && <> · by {e.meta.actor_name}</>}
            </>
          )}
          {e.kind === 'expense_submitted' && e.meta?.amount_cents != null && (
            <> · {e.meta.expense_kind} · {centsToUsd(e.meta.amount_cents)}</>
          )}
        </div>
      </div>
    </li>
  );
}

function fmtRelative(ms) {
  const dms = Date.now() - ms;
  if (dms < 60_000) return 'just now';
  if (dms < 3_600_000) return `${Math.floor(dms / 60_000)}m ago`;
  if (dms < 86_400_000) return `${Math.floor(dms / 3_600_000)}h ago`;
  return `${Math.floor(dms / 86_400_000)}d ago`;
}

// ─── Leaflet map ──────────────────────────────────────────────────────────
// Loaded lazily from a CDN inside useEffect so it isn't bundled with the
// SPA. The container ref holds the map instance for cleanup on unmount.
function LiveMap({ openShifts, jobs, violations }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const L = await loadLeaflet();
        if (cancelled || !containerRef.current) return;
        if (!mapRef.current) {
          mapRef.current = L.map(containerRef.current, {
            zoomControl: true, scrollWheelZoom: true,
          }).setView([39.5, -98.35], 4); // CONUS-ish default
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '© OpenStreetMap',
          }).addTo(mapRef.current);
        }
        renderMarkers(L, mapRef.current, layerRef, { openShifts, jobs, violations });
        setStatus('ready');
      } catch (err) {
        console.error('Leaflet load failed', err);
        setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [openShifts, jobs, violations]);

  return (
    <div className="live-map-wrap">
      <div ref={containerRef} className="live-map" />
      {status === 'loading' && <div className="map-overlay">Loading map…</div>}
      {status === 'error' && <div className="map-overlay">Map unavailable (offline?). Feed still works ↖.</div>}
    </div>
  );
}

function renderMarkers(L, map, layerRef, { openShifts, jobs, violations }) {
  if (layerRef.current) layerRef.current.remove();
  const layer = L.layerGroup().addTo(map);

  // Job sites — small grey circles + label.
  for (const j of jobs) {
    if (j.site_lat == null || j.site_lng == null) continue;
    L.circle([j.site_lat, j.site_lng], {
      radius: j.geofence_radius_m || 100,
      color: 'rgba(180, 180, 180, 0.5)', weight: 1, fillOpacity: 0.05,
    }).addTo(layer);
    L.marker([j.site_lat, j.site_lng], { title: j.name }).addTo(layer)
      .bindPopup(`<b>${escapeHtml(j.name)}</b><br>job site`);
  }

  // Open shifts — green pins.
  for (const s of openShifts) {
    if (s.clock_in_lat == null || s.clock_in_lng == null) continue;
    L.circleMarker([s.clock_in_lat, s.clock_in_lng], {
      radius: 8, color: '#4ade80', fillColor: '#4ade80', fillOpacity: 0.8,
    }).addTo(layer).bindPopup(
      `<b>${escapeHtml(s.user_name)}</b><br>on shift${s.job_name ? ` · ${escapeHtml(s.job_name)}` : ''}`,
    );
  }

  // Recent geofence violations — red pins.
  for (const v of violations) {
    if (v.attempted_lat == null || v.attempted_lng == null) continue;
    L.circleMarker([v.attempted_lat, v.attempted_lng], {
      radius: 6, color: '#ef4444', fillColor: '#ef4444', fillOpacity: 0.7,
    }).addTo(layer).bindPopup(
      `<b>${escapeHtml(v.user_name)}</b><br>geofence violation · ${v.distance_m}m from ${escapeHtml(v.job_name || 'site')}`,
    );
  }

  layerRef.current = layer;

  // Fit to markers if we have any geo points.
  const allPoints = [
    ...jobs.filter(j => j.site_lat != null).map(j => [j.site_lat, j.site_lng]),
    ...openShifts.filter(s => s.clock_in_lat != null).map(s => [s.clock_in_lat, s.clock_in_lng]),
    ...violations.filter(v => v.attempted_lat != null).map(v => [v.attempted_lat, v.attempted_lng]),
  ];
  if (allPoints.length >= 2) {
    map.fitBounds(allPoints, { padding: [40, 40], maxZoom: 15 });
  } else if (allPoints.length === 1) {
    map.setView(allPoints[0], 13);
  }
}

function escapeHtml(s) {
  return String(s || '').replace(/[&<>"']/g, ch => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
  ));
}

// Idempotent loader — caches the L module after first import.
let leafletPromise = null;
function loadLeaflet() {
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('no window'));
    if (window.L) return resolve(window.L);
    // CSS
    if (!document.querySelector('link[data-leaflet-css]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      link.setAttribute('data-leaflet-css', '');
      document.head.appendChild(link);
    }
    // JS
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error('leaflet script failed'));
    document.head.appendChild(script);
  });
  return leafletPromise;
}
